import React, { useState } from 'react';
import { Input, Button, Tag, Spin, Modal, message, Tooltip, Badge } from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, FilterOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined, CheckCircleOutlined, StopOutlined, EyeOutlined, FileTextOutlined, ShoppingOutlined, MessageOutlined } from '@ant-design/icons';
import { Drawer, List, Card, Typography, Statistic } from 'antd';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { customerService, Customer } from '../../services/customerService';
import CustomerModal from '../../components/customers/CustomerModalMobile';
import styles from './CustomersPageMobile.module.css';

const { Text } = Typography;

// Hàm loại bỏ dấu tiếng Việt
function removeVietnameseTones(str: string) {
  return str
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
}

const CustomersPageMobile: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const queryClient = useQueryClient();

  // Query danh sách khách hàng
  const { data: customersData = { data: [] }, isLoading } = useQuery(
    ['customers', searchText],
    () => customerService.getCustomers({
      search: searchText,
      page: 1,
      limit: 1000
    }),
    { staleTime: 5 * 60 * 1000 }
  );
  const customers = customersData.data || [];

  // Query chi tiết khách hàng
  const { data: viewDetailData, isLoading: isLoadingViewDetail } = useQuery(
    ['customer-detail', selectedCustomer?.id],
    () => selectedCustomer ? customerService.getCustomerDetail(selectedCustomer.id) : null,
    {
      enabled: !!selectedCustomer?.id && drawerVisible,
      staleTime: 1 * 60 * 1000,
    }
  );

  // Mutation xóa khách hàng
  const deleteMutation = useMutation((id: number) => customerService.deleteCustomer(id), {
    onSuccess: () => {
      message.success('Xóa khách hàng thành công!');
      queryClient.invalidateQueries('customers');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra khi xóa khách hàng');
    }
  });

  // Mutation tạo/cập nhật khách hàng
  const createMutation = useMutation((data: any) => customerService.createCustomer(data), {
    onSuccess: () => {
      message.success('Tạo khách hàng thành công!');
      queryClient.invalidateQueries('customers');
      setModalVisible(false);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra khi tạo khách hàng');
    }
  });
  const updateMutation = useMutation(({ id, data }: { id: number; data: any }) => customerService.updateCustomer(id, data), {
    onSuccess: () => {
      message.success('Cập nhật khách hàng thành công!');
      queryClient.invalidateQueries('customers');
      setModalVisible(false);
      setEditingCustomer(null);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật khách hàng');
    }
  });

  const handleAdd = () => {
    setEditingCustomer(null);
    setModalVisible(true);
  };
  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setModalVisible(true);
  };
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xóa khách hàng này?',
      content: 'Hành động này không thể hoàn tác.',
      okText: 'Có, Xóa',
      okType: 'danger',
      cancelText: 'Không',
      onOk: () => deleteMutation.mutate(id),
    });
  };
  const handleView = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDrawerVisible(true);
  };

  const handleCallPhone = (phoneNumber: string) => {
    if (phoneNumber) {
      // Loại bỏ tất cả ký tự không phải số
      const cleanPhone = phoneNumber.replace(/[^0-9+]/g, '');
      if (cleanPhone) {
        window.open(`tel:${cleanPhone}`, '_self');
      }
    }
  };

  const handleFindZalo = (phoneNumber: string) => {
    if (phoneNumber) {
      // Loại bỏ tất cả ký tự không phải số
      const cleanPhone = phoneNumber.replace(/[^0-9]/g, '');
      if (cleanPhone) {
        // Mở Zalo với số điện thoại
        window.open(`https://zalo.me/${cleanPhone}`, '_blank');
      }
    }
  };

  const handleSubmit = async (values: any) => {
    if (editingCustomer) {
      await updateMutation.mutateAsync({ id: editingCustomer.id, data: values });
    } else {
      await createMutation.mutateAsync(values);
    }
  };
  const handleFilterChange = (key: string, value: string) => {
    // setFilters(prev => ({ ...prev, [key]: value })); // This line is removed
  };
  const clearFilters = () => {
    // setFilters({ status: '' }); // This line is removed
    setSearchText('');
  };

  // Lọc dữ liệu theo searchText (không phân biệt hoa thường, không dấu, tương tự DebtsPageMobile)
  const filteredCustomers = customers.filter((customer: Customer) => {
    if (!searchText) return true;
    const search = removeVietnameseTones(searchText).toLowerCase();
    // Nếu searchText là số, so sánh với mã KH hoặc id (chuyển về chữ thường)
    if (!isNaN(Number(searchText))) {
      return (
        String(customer.code).toLowerCase().includes(searchText.toLowerCase()) ||
        String(customer.id).toLowerCase().includes(searchText.toLowerCase())
      );
    }
    // Nếu searchText là text, so sánh với các trường không dấu, đã chuyển về chữ thường
    return (
      removeVietnameseTones(customer.name || '').toLowerCase().includes(search) ||
      removeVietnameseTones(customer.code || '').toLowerCase().includes(search) ||
      removeVietnameseTones(customer.phone || '').toLowerCase().includes(search) ||
      removeVietnameseTones(customer.email || '').toLowerCase().includes(search)
    );
  });

  return (
    <div className={styles['customer-mobile-container']}>
      {/* HEADER */}
      <header className={styles['customer-mobile-header']}>
        {/* Thanh tìm kiếm */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Input
            className={styles['customer-mobile-search']}
            style={{ width: '100%', padding: '12px 40px 12px 16px', border: '1px solid #ced4da', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
            placeholder="Tìm kiếm khách hàng..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <span style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 18 }}>
            <ReloadOutlined />
          </span>
        </div>
        {/* Nút làm mới */}
        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            style={{ flexGrow: 1, padding: 12, fontSize: 16, fontWeight: 600, borderRadius: 8, border: '1px solid #ced4da', color: '#212529', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries('customers')}
          >
            Làm mới
          </Button>
        </div>
        {/* Bộ lọc đã bị loại bỏ */}
      </header>
      <Spin spinning={isLoading}>
        <div className={styles['customer-mobile-list']}>
          {filteredCustomers.map((item: Customer) => (
            <div
              key={item.id}
              className={styles['customer-mobile-card']}
              style={{
                maxWidth: 420,
                width: '100%',
                background: '#fff',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #e5e7eb',
                margin: '0 auto 4px',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#1890ff', textAlign: 'left' }}>{item.name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, textAlign: 'left' }}>Mã KH: {item.code}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 15, textAlign: 'left' }}>
                  {item.contact_person && <div><strong>Người liên hệ:</strong> {item.contact_person}</div>}
                  {item.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <PhoneOutlined />
                      <span>{item.phone}</span>
                    </div>
                  )}
                  {item.email && <div><MailOutlined /> {item.email}</div>}
                  {item.address && <div><EnvironmentOutlined /> {item.address}</div>}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Badge status={Number(item.total_orders) > 0 ? 'success' : 'default'} text={`Đơn: ${Number(item.total_orders) || 0}`} />
                    <Badge status={Number(item.total_amount) > 0 ? 'success' : 'default'} text={`Tổng: ${(Number(item.total_amount) || 0).toLocaleString('vi-VN')} VNĐ`} />
                  </div>
                </div>
              </div>
              <div style={{ background: '#f9fafb', padding: '12px 24px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                  {item.phone && (
                    <>
                      <button 
                        onClick={() => handleFindZalo(item.phone || '')}
                        style={{ 
                          padding: 8, 
                          borderRadius: '50%', 
                          color: '#0068ff', 
                          background: 'none', 
                          border: 'none', 
                          fontSize: 20, 
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        title="Tìm Zalo"
                      >
                        <MessageOutlined />
                      </button>
                      <button 
                        onClick={() => handleCallPhone(item.phone || '')}
                        style={{ 
                          padding: 8, 
                          borderRadius: '50%', 
                          color: '#1890ff', 
                          background: 'none', 
                          border: 'none', 
                          fontSize: 20, 
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        title="Gọi điện"
                      >
                        <PhoneOutlined />
                      </button>
                    </>
                  )}
                  <button onClick={() => handleView(item)} className={styles['customer-mobile-view-btn']} style={{ padding: 8, borderRadius: '50%', color: '#1890ff', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <EyeOutlined />
                  </button>
                  <button onClick={() => handleEdit(item)} className={styles['customer-mobile-edit-btn']} style={{ padding: 8, borderRadius: '50%', color: '#faad14', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <EditOutlined />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className={styles['customer-mobile-delete-btn']} style={{ padding: 8, borderRadius: '50%', color: 'red', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <DeleteOutlined />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filteredCustomers.length === 0 && (
            <div className={styles['customer-mobile-empty']}>Không có khách hàng nào phù hợp.</div>
          )}
        </div>
      </Spin>
      {/* Modal thêm/sửa khách hàng */}
      <CustomerModal
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingCustomer(null);
        }}
        onSubmit={handleSubmit}
        editingCustomer={editingCustomer}
        loading={createMutation.isLoading || updateMutation.isLoading}
      />
      {/* Floating Add Button */}
      <button
        onClick={handleAdd}
        className={styles['customer-mobile-add-btn']}
        style={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: 100,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#0d6efd',
          color: '#fff',
          border: 'none',
          boxShadow: '0 4px 16px rgba(13,110,253,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          cursor: 'pointer',
          transition: 'background 0.2s',
        }}
        title="Thêm khách hàng mới"
      >
        <PlusOutlined />
      </button>
      {/* Drawer chi tiết khách hàng */}
      <Drawer
        title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FileTextOutlined /> Chi tiết khách hàng {isLoadingViewDetail && (<Text type="secondary" style={{ fontSize: '14px', marginLeft: '8px' }}>(Đang tải...)</Text>)}</div>}
        placement="bottom"
        height="90vh"
        onClose={() => {
          setDrawerVisible(false);
          setSelectedCustomer(null);
        }}
        open={drawerVisible}
        bodyStyle={{ padding: 12, background: '#f5f6fa' }}
        destroyOnClose
      >
        {isLoadingViewDetail ? (
          <div style={{ textAlign: 'center', padding: 32 }}>Đang tải...</div>
        ) : viewDetailData ? (
          <div>
            <Card size="small" style={{ marginBottom: 16, borderRadius: 12 }}>
              <Statistic title="Mã khách hàng" value={viewDetailData.customer.code} valueStyle={{ fontSize: '16px' }} />
              <Statistic title="Tổng đơn hàng" value={viewDetailData.customer.total_orders || 0} prefix={<ShoppingOutlined />} valueStyle={{ color: '#52c41a' }} />
            </Card>
            <Card size="small" style={{ marginBottom: 16, borderRadius: 12 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Tên khách hàng:</Text></div>
              <div>{viewDetailData.customer.name}</div>
              {viewDetailData.customer.contact_person && (<div style={{ fontSize: '12px', color: '#666' }}>Người liên hệ: {viewDetailData.customer.contact_person}</div>)}
              <div style={{ marginTop: 8 }}><Text strong>Trạng thái:</Text> <Tag color={viewDetailData.customer.status === 'active' ? 'green' : 'red'}>{viewDetailData.customer.status === 'active' ? 'Hoạt động' : 'Không hoạt động'}</Tag></div>
              {viewDetailData.customer.address && (<div style={{ marginTop: 8 }}><Text strong>Địa chỉ:</Text> <span>{viewDetailData.customer.address}</span></div>)}
            </Card>
            <Card size="small" style={{ marginBottom: 16, borderRadius: 12 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Thông tin liên hệ:</Text></div>
              <List
                dataSource={[
                  { label: 'Số điện thoại', value: viewDetailData.customer.phone, icon: <PhoneOutlined /> },
                  { label: 'Email', value: viewDetailData.customer.email, icon: <MailOutlined /> },
                  { label: 'Mã số thuế', value: viewDetailData.customer.tax_code, icon: <FileTextOutlined /> }
                ].filter(item => item.value)}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta avatar={item.icon} title={item.label} description={item.value} />
                  </List.Item>
                )}
              />
            </Card>
            {viewDetailData.orders && viewDetailData.orders.length > 0 && (
              <Card size="small" style={{ marginBottom: 16, borderRadius: 12 }}>
                <div style={{ marginBottom: 8 }}><Text strong>Lịch sử đơn hàng gần đây:</Text></div>
                <List
                  dataSource={viewDetailData.orders}
                  renderItem={(order: any) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`Đơn hàng ${order.voucher_number || order.reference_number}`}
                        description={
                          <div>
                            <div>Ngày: {dayjs(order.created_at).format('DD/MM/YYYY')}</div>
                            <div>Tổng tiền: {(order.total_amount || 0).toLocaleString('vi-VN')} VNĐ</div>
                            <div>Trạng thái: <Tag color={order.status === 'completed' ? 'green' : 'orange'} style={{ marginLeft: 4 }}>{order.status === 'completed' ? 'Hoàn thành' : 'Chờ xử lý'}</Tag></div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            )}
            {viewDetailData.customer.notes && (
              <Card size="small" style={{ borderRadius: 12 }}>
                <div style={{ marginBottom: 8 }}><Text strong>Ghi chú:</Text></div>
                <div>{viewDetailData.customer.notes}</div>
              </Card>
            )}
          </div>
        ) : null}
      </Drawer>
    </div>
  );
};

export default CustomersPageMobile;
